import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { pickingService } from '../../../../../services/pickingService';
import { OrderSSCCDto } from '../../../../../types/picking';
import { ScanTextInput, NumericInput } from '../../../../common/inputs';

interface EnrichSSCCModalProps {
  pListId: number;
  sscc: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * «Berik SSCC» — lar operatøren sette/justere feltene som lagres direkte på OrderSSCC
 * og skrives ut på pall-etiketten: GTIN, antall, batchnr, utløpsdato, topplast og bruttovekt.
 * Tom GTIN er greit — etiketten henter da ekte GTIN fra varens strekkode (Items.ItemBarCode).
 *
 * For Flemming Egg har en pall normalt én OrderSSCC-rad (én ordrelinje pr. SSCC). Hvis pallen
 * unntaksvis har flere rader, redigeres den første (laveste ID).
 */
export const EnrichSSCCModal: React.FC<EnrichSSCCModalProps> = ({ pListId, sscc, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [row, setRow] = useState<OrderSSCCDto | null>(null);

  const [gtin, setGtin] = useState('');
  const [quantity, setQuantity] = useState('');
  const [batchNo, setBatchNo] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [stackingWeight, setStackingWeight] = useState('');
  const [grossWeight, setGrossWeight] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await pickingService.getOrderSSCCs(pListId);
        const matches = all.filter(r => r.sscc === sscc).sort((a, b) => a.id - b.id);
        if (cancelled) return;
        const r = matches[0] || null;
        setRow(r);
        if (r) {
          setGtin(r.gtin || '');
          setQuantity(r.quantity != null ? String(r.quantity) : '');
          setBatchNo(r.itemBatchNo || '');
          setExpiryDate(r.itemBatchExpDate ? r.itemBatchExpDate.substring(0, 10) : '');
          setStackingWeight(r.stackingWeight != null ? String(r.stackingWeight) : '');
          setGrossWeight(r.grossWeight != null ? String(r.grossWeight) : '');
        } else {
          setError(t('warehouse.modules.picking.enrichSscc.notFound', {}, 'Fant ingen SSCC-rad for denne pallen.'));
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || t('warehouse.modules.picking.enrichSscc.loadFailed', {}, 'Kunne ikke laste pall-data.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pListId, sscc, t]);

  const handleSave = async () => {
    if (!row) return;
    setSaving(true);
    setError(null);
    try {
      await pickingService.updateOrderSSCC(pListId, row.id, {
        quantity: quantity === '' ? 0 : parseFloat(quantity),
        gtin: gtin.trim() === '' ? null : gtin.trim(),
        itemBatchNo: batchNo.trim() === '' ? null : batchNo.trim(),
        itemBatchExpDate: expiryDate === '' ? null : `${expiryDate}T12:00:00`,
        stackingWeight: stackingWeight === '' ? null : parseInt(stackingWeight, 10),
        grossWeight: grossWeight === '' ? null : parseFloat(grossWeight)
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || t('warehouse.modules.picking.enrichSscc.saveFailed', {}, 'Kunne ikke lagre.'));
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const inputCls = 'w-full p-3 sm:p-2 border border-gray-300 rounded min-h-[44px]';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            {t('warehouse.modules.picking.enrichSscc.title', {}, 'Berik pall')}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label={t('common.close', {}, 'Lukk')}>
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-xs text-gray-500 break-all">SSCC: {sscc}</p>

          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : (
            <>
              <div>
                <label className={labelCls}>{t('warehouse.modules.picking.enrichSscc.gtin', {}, 'GTIN')}</label>
                <ScanTextInput value={gtin} onChange={(e) => setGtin(e.target.value)} className={inputCls}
                  placeholder={t('warehouse.modules.picking.enrichSscc.gtinPlaceholder', {}, 'Tom = bruk varens strekkode')} />
              </div>

              <div>
                <label className={labelCls}>{t('warehouse.modules.picking.enrichSscc.quantity', {}, 'Antall (D-pak)')}</label>
                <NumericInput mode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} className={inputCls} />
              </div>

              <div>
                <label className={labelCls}>{t('warehouse.modules.picking.enrichSscc.batchNo', {}, 'Batchnr')}</label>
                <ScanTextInput value={batchNo} onChange={(e) => setBatchNo(e.target.value)} className={inputCls} />
              </div>

              <div>
                <label className={labelCls}>{t('warehouse.modules.picking.enrichSscc.expiryDate', {}, 'Best før')}</label>
                <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={inputCls} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>{t('warehouse.modules.picking.enrichSscc.stackingWeight', {}, 'Topplast (kg)')}</label>
                  <NumericInput mode="integer" value={stackingWeight} onChange={(e) => setStackingWeight(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{t('warehouse.modules.picking.enrichSscc.grossWeight', {}, 'Bruttovekt (kg)')}</label>
                  <NumericInput mode="decimal" value={grossWeight} onChange={(e) => setGrossWeight(e.target.value)} className={inputCls} />
                </div>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
            </>
          )}
        </div>

        <div className="flex gap-2 p-4 border-t">
          <button onClick={onClose} disabled={saving}
            className="flex-1 py-3 sm:py-2 rounded border border-gray-300 hover:bg-gray-50 font-medium min-h-[44px] disabled:opacity-50">
            {t('common.cancel', {}, 'Avbryt')}
          </button>
          <button onClick={handleSave} disabled={loading || saving || !row}
            className="flex-1 bg-[#0E7C86] text-white py-3 sm:py-2 rounded hover:bg-[#0A5F66] font-medium min-h-[44px] disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 size={16} className="animate-spin" />}
            {t('common.save', {}, 'Lagre')}
          </button>
        </div>
      </div>
    </div>
  );
};
